import type {
  AiManagedGroupActivity,
  AiManagedGroupMember,
  AiManagedGroupMemberAnalytics,
  AiManagedGroupPolicy,
  CampaignAnalyticsGranularity,
} from "@adpilot/shared";
import { MAX_MANAGED_GROUP_MEMBERS } from "@adpilot/shared";
import {
  IconActivity,
  IconArchive,
  IconArrowLeft,
  IconArrowsExchange,
  IconDotsVertical,
  IconEdit,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlus,
  IconSparkles,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PendingApprovalsSection } from "@/components/ai-operations/PendingApprovalsSection";
import { EditManagedGroupDialog } from "@/components/managed-group/EditManagedGroupDialog";
import {
  type CandidateAiFilter,
  type CandidateDeliveryFilter,
  type CandidatePortfolioFilter,
  ManagedGroupCandidatePicker,
} from "@/components/managed-group/ManagedGroupCandidatePicker";
import { ManagedGroupPerformancePanel } from "@/components/managed-group/ManagedGroupPerformancePanel";
import { ManagedGroupStatusBadge } from "@/components/managed-group/ManagedGroupStatusBadge";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { PageTabs } from "@/components/patterns/PageTabs";
import { ReportingFilters } from "@/components/patterns/reporting-filters";
import { SearchInput } from "@/components/patterns/SearchInput";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import type { PerformanceMetric } from "@/components/shared/performance/performance";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TabsContent } from "@/components/ui/tabs";
import { usePermission } from "@/hooks/use-permission";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { useFormatters } from "@/lib/format";
import {
  addManagedGroupCampaigns,
  changeManagedGroupLifecycle,
  fetchEligibleManagedGroupCampaigns,
  fetchManagedGroupDetailAnalytics,
  fetchManagedGroupPortfolioOptions,
  fetchManagedGroups,
  moveManagedGroupCampaigns,
  removeManagedGroupCampaigns,
  setManagedGroupCampaignAi,
  updateManagedGroup,
} from "@/lib/managed-group-api";
import { requireModule } from "@/lib/module-access";
import { selectResourceShopForNavigation } from "@/lib/resource-navigation";
import { useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";

export const Route = createFileRoute("/_authed/managed-groups_/$id")({
  beforeLoad: () => {
    requireModule("ai");
  },
  component: ManagedGroupDetailPage,
});

type DetailTab = "overview" | "campaigns" | "pending";
type CampaignAiFilter = "all" | "on" | "off";

const MEMBER_PAGE_SIZE = 6;

function ManagedGroupDetailPage(): React.ReactElement {
  const { id } = Route.useParams();
  const { t } = useTranslation("managedGroups");
  const navigate = useNavigate();
  const { formatCurrency, formatDateTime, formatPercent } = useFormatters();
  const { hasPermission } = usePermission();
  const canManage = hasPermission("manage_ai");
  const range = useReportingPeriodStore((state) => state.period);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const [metric, setMetric] = useState<PerformanceMetric>("spend");
  const [granularity, setGranularity] = useState<CampaignAnalyticsGranularity>("day");
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [addPage, setAddPage] = useState(1);
  const [addSearch, setAddSearch] = useState("");
  const [addDeliveryStatus, setAddDeliveryStatus] = useState<CandidateDeliveryFilter>("all");
  const [addPortfolioId, setAddPortfolioId] = useState<CandidatePortfolioFilter>("all");
  const [addAiStatus, setAddAiStatus] = useState<CandidateAiFilter>("all");
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [moveMember, setMoveMember] = useState<AiManagedGroupMember | null>(null);
  const [removeMember, setRemoveMember] = useState<AiManagedGroupMember | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>("overview");
  const [memberSearch, setMemberSearch] = useState("");
  const [memberAiFilter, setMemberAiFilter] = useState<CampaignAiFilter>("all");
  const [memberPage, setMemberPage] = useState(1);
  const query = useQuery({
    queryKey: ["ai-managed-groups", "detail", id, range, timeZone, granularity],
    queryFn: () => fetchManagedGroupDetailAnalytics(id, range, granularity, timeZone),
  });
  const groupsQuery = useQuery({
    queryKey: ["ai-managed-groups", "shop", query.data?.group.shopId],
    queryFn: () => fetchManagedGroups(query.data!.group.shopId),
    enabled: Boolean(query.data?.group.shopId),
  });
  const eligibleQuery = useQuery({
    queryKey: [
      "ai-managed-groups",
      "eligible-campaigns",
      query.data?.group.shopId,
      addPage,
      addSearch,
      addDeliveryStatus,
      addPortfolioId,
      addAiStatus,
    ],
    queryFn: () =>
      fetchEligibleManagedGroupCampaigns(query.data!.group.shopId, {
        page: addPage,
        search: addSearch.trim(),
        status: addDeliveryStatus === "all" ? undefined : addDeliveryStatus,
        portfolioId: addPortfolioId === "all" ? undefined : addPortfolioId,
        aiStatus: addAiStatus === "all" ? undefined : addAiStatus,
      }),
    enabled: addOpen && Boolean(query.data?.group.shopId),
  });
  const portfolioOptionsQuery = useQuery({
    queryKey: ["ai-managed-groups", "portfolio-options", query.data?.group.shopId],
    queryFn: () => fetchManagedGroupPortfolioOptions(query.data!.group.shopId),
    enabled: addOpen && Boolean(query.data?.group.shopId),
  });
  const refreshGroups = async () => {
    await queryClient.invalidateQueries({ queryKey: ["ai-managed-groups"] });
    await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const addMutation = useMutation({
    mutationFn: () =>
      addManagedGroupCampaigns(id, {
        campaignIds: selectedCampaignIds,
        expectedVersion: query.data!.group.version,
      }),
    onSuccess: async () => {
      setAddOpen(false);
      setAddPage(1);
      setAddSearch("");
      setAddDeliveryStatus("all");
      setAddPortfolioId("all");
      setAddAiStatus("all");
      setSelectedCampaignIds([]);
      await refreshGroups();
    },
  });
  const removeMutation = useMutation({
    mutationFn: (member: AiManagedGroupMember) =>
      removeManagedGroupCampaigns(id, {
        campaignIds: [member.campaignId],
        expectedVersion: query.data!.group.version,
      }),
    onSuccess: async () => {
      setRemoveMember(null);
      await refreshGroups();
    },
  });
  const moveMutation = useMutation({
    mutationFn: (targetGroupId: string) => {
      const target = groupsQuery.data?.find((group) => group.id === targetGroupId);
      if (!moveMember || !target) throw new Error("Move target is unavailable");
      return moveManagedGroupCampaigns(id, {
        campaignIds: [moveMember.campaignId],
        targetGroupId,
        sourceExpectedVersion: query.data!.group.version,
        targetExpectedVersion: target.version,
      });
    },
    onSuccess: async () => {
      setMoveMember(null);
      await refreshGroups();
    },
  });
  const aiMutation = useMutation({
    mutationFn: (member: AiManagedGroupMember) =>
      setManagedGroupCampaignAi(id, member.campaignId, {
        aiManaged: !member.aiManaged,
        expectedVersion: query.data!.group.version,
      }),
    onSuccess: refreshGroups,
  });
  const editMutation = useMutation({
    mutationFn: (value: { name: string; policy: AiManagedGroupPolicy }) =>
      updateManagedGroup(id, {
        ...value,
        expectedVersion: query.data!.group.version,
      }),
    onSuccess: async () => {
      setEditOpen(false);
      await refreshGroups();
    },
  });
  const lifecycleMutation = useMutation({
    mutationFn: (action: "activate" | "pause" | "archive") =>
      changeManagedGroupLifecycle(id, {
        action,
        expectedVersion: query.data!.group.version,
      }),
    onSuccess: async () => {
      setPauseOpen(false);
      setArchiveOpen(false);
      await refreshGroups();
    },
  });

  if (query.isLoading) {
    return (
      <div className="space-y-4 px-6 py-6">
        <Skeleton className="h-14 w-80" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return <ErrorState message={t("detail.loadFailed")} onRetry={() => void query.refetch()} />;
  }
  const { group, members, activity, kpis, trend, dataThrough, shopName, currency } = query.data;
  const normalizedMemberSearch = memberSearch.trim().toLocaleLowerCase();
  const filteredMembers = members.filter((member) => {
    if (
      normalizedMemberSearch &&
      !member.name.toLocaleLowerCase().includes(normalizedMemberSearch)
    ) {
      return false;
    }
    if (memberAiFilter === "on") return member.aiManaged;
    if (memberAiFilter === "off") return !member.aiManaged;
    return true;
  });
  const pendingDecisionCount = members.reduce(
    (total, member) => total + member.pendingDecisions,
    0,
  );
  const memberPageCount = Math.max(1, Math.ceil(filteredMembers.length / MEMBER_PAGE_SIZE));
  const safeMemberPage = Math.min(memberPage, memberPageCount);
  const pagedMembers = filteredMembers.slice(
    (safeMemberPage - 1) * MEMBER_PAGE_SIZE,
    safeMemberPage * MEMBER_PAGE_SIZE,
  );

  return (
    <div className="flex min-h-full w-full shrink-0 flex-col gap-[18px] px-6 pb-6 pt-[22px]">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <Link
            to="/managed-groups"
            className="mb-2 flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            <IconArrowLeft className="size-3.5" />
            {t("actions.backToList")}
          </Link>
          <div className="flex min-w-0 items-center gap-2.5">
            <h1 className="truncate text-[22px] font-semibold leading-7 text-ink">{group.name}</h1>
            <ManagedGroupStatusBadge status={group.effectiveStatus} />
          </div>
          <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
            {t("detail.subtitleWithShop", {
              count: group.memberCount,
              shop: shopName ?? t("detail.unknownShop"),
            })}
          </p>
        </div>
        <div className="flex max-w-full flex-col items-end gap-3">
          <ReportingFilters scope="resource" />
          {canManage && group.lifecycleStatus !== "archived" ? (
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <IconEdit data-icon="inline-start" />
                {t("edit.button")}
              </Button>
              {group.lifecycleStatus === "active" ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={lifecycleMutation.isPending}
                  onClick={() => setPauseOpen(true)}
                >
                  <IconPlayerPause data-icon="inline-start" />
                  {t("lifecycle.pause")}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!group.workspaceAiEnabled || lifecycleMutation.isPending}
                  onClick={() => lifecycleMutation.mutate("activate")}
                >
                  <IconPlayerPlay data-icon="inline-start" />
                  {t("lifecycle.resume")}
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button variant="outline" size="icon-sm" aria-label={t("detail.moreActions")} />
                  }
                >
                  <IconDotsVertical />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      variant="destructive"
                      disabled={group.lifecycleStatus !== "paused" || lifecycleMutation.isPending}
                      onClick={() => setArchiveOpen(true)}
                    >
                      <IconArchive />
                      {t("lifecycle.archive")}
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}
        </div>
      </header>

      <PageTabs
        density="compact"
        className="shrink-0 gap-0"
        value={activeTab}
        action={
          <Button
            render={
              <Link
                to="/ai-operations"
                search={{ ...DEFAULT_APPROVAL_SEARCH, managedGroupId: group.id }}
              />
            }
            variant="ghost"
            size="xs"
          >
            <IconActivity data-icon="inline-start" />
            {t("detail.tabs.activity")}
          </Button>
        }
        items={[
          { value: "overview", label: t("detail.tabs.overview") },
          { value: "campaigns", label: t("detail.tabs.campaigns"), count: group.memberCount },
          {
            value: "pending",
            label: t("detail.tabs.pending"),
            count: pendingDecisionCount,
          },
        ]}
        onValueChange={(value) => {
          if (value === "overview" || value === "campaigns" || value === "pending") {
            setActiveTab(value);
          }
        }}
      >
        <TabsContent value="overview" className="flex flex-col gap-[18px]">
          <ManagedGroupPerformancePanel
            detail
            kpis={kpis}
            trend={trend}
            dataThrough={dataThrough}
            metric={metric}
            granularity={granularity}
            onMetricChange={setMetric}
            onGranularityChange={setGranularity}
            isLoading={false}
            isFetching={query.isFetching}
            isError={query.isError}
            onRetry={() => void query.refetch()}
          />
          <RecentActivityPanel
            activity={activity.slice(0, 2)}
            formatDateTime={formatDateTime}
            onOpenAll={() =>
              void navigate({
                to: "/ai-operations",
                search: { ...DEFAULT_APPROVAL_SEARCH, managedGroupId: group.id },
              })
            }
          />
        </TabsContent>

        <TabsContent value="campaigns">
          <CampaignsPanel
            shopId={group.shopId}
            lifecycleStatus={group.lifecycleStatus}
            totalCount={members.length}
            filteredCount={filteredMembers.length}
            visibleMembers={pagedMembers}
            search={memberSearch}
            aiFilter={memberAiFilter}
            page={safeMemberPage}
            pageCount={memberPageCount}
            canManage={canManage}
            aiChanging={aiMutation.isPending}
            currency={currency ?? "USD"}
            formatCurrency={formatCurrency}
            formatDateTime={formatDateTime}
            formatPercent={formatPercent}
            onSearchChange={(value) => {
              setMemberSearch(value);
              setMemberPage(1);
            }}
            onAiFilterChange={(value) => {
              setMemberAiFilter(value);
              setMemberPage(1);
            }}
            onPageChange={setMemberPage}
            onAdd={() => setAddOpen(true)}
            onToggleAi={(member) => aiMutation.mutate(member)}
            onMove={setMoveMember}
            onRemove={setRemoveMember}
          />
        </TabsContent>

        <TabsContent value="pending">
          <PendingApprovalsSection managedGroupId={group.id} variant="managed-group" />
        </TabsContent>
      </PageTabs>

      <Dialog
        open={addOpen}
        onOpenChange={(nextOpen) => {
          setAddOpen(nextOpen);
          if (!nextOpen) {
            setAddPage(1);
            setAddSearch("");
            setAddDeliveryStatus("all");
            setAddPortfolioId("all");
            setAddAiStatus("all");
            setSelectedCampaignIds([]);
          }
        }}
      >
        <DialogPopup className="h-[680px] w-[780px] max-w-[calc(100vw-32px)] overflow-hidden">
          <DialogHeader>
            <DialogTitle>{t("detail.addCampaigns")}</DialogTitle>
            <DialogDescription>{t("detail.addCampaignsDescription")}</DialogDescription>
          </DialogHeader>
          <ManagedGroupCandidatePicker
            className="mx-6 min-h-0 rounded-[10px] border border-line"
            campaigns={eligibleQuery.data?.data ?? []}
            portfolioOptions={portfolioOptionsQuery.data ?? []}
            search={addSearch}
            deliveryStatus={addDeliveryStatus}
            portfolioId={addPortfolioId}
            aiStatus={addAiStatus}
            checkedIds={new Set(selectedCampaignIds)}
            page={addPage}
            pageSize={eligibleQuery.data?.pagination.pageSize ?? 10}
            total={eligibleQuery.data?.pagination.total ?? 0}
            isLoading={
              eligibleQuery.isLoading ||
              eligibleQuery.isFetching ||
              portfolioOptionsQuery.isLoading ||
              portfolioOptionsQuery.isFetching
            }
            isCampaignError={eligibleQuery.isError}
            isPortfolioError={portfolioOptionsQuery.isError}
            emptyText={t("detail.noEligibleCampaigns")}
            onSearchChange={(value) => {
              setAddSearch(value);
              setAddPage(1);
            }}
            onDeliveryStatusChange={(value) => {
              setAddDeliveryStatus(value);
              setAddPage(1);
            }}
            onPortfolioIdChange={(value) => {
              setAddPortfolioId(value);
              setAddPage(1);
            }}
            onAiStatusChange={(value) => {
              setAddAiStatus(value);
              setAddPage(1);
            }}
            onPageChange={setAddPage}
            onRetryCampaigns={() => void eligibleQuery.refetch()}
            onRetryPortfolios={() => void portfolioOptionsQuery.refetch()}
            onToggle={(campaign) =>
              setSelectedCampaignIds((current) =>
                current.includes(campaign.id)
                  ? current.filter((id) => id !== campaign.id)
                  : [...current, campaign.id],
              )
            }
            onToggleAll={(checked, selectableCampaigns) =>
              setSelectedCampaignIds((current) => {
                const next = new Set(current);
                for (const campaign of selectableCampaigns) {
                  if (checked) next.add(campaign.id);
                  else next.delete(campaign.id);
                }
                return [...next];
              })
            }
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              {t("actions.close")}
            </Button>
            <Button
              disabled={selectedCampaignIds.length === 0 || addMutation.isPending}
              onClick={() => addMutation.mutate()}
            >
              {t("detail.addSelected", { count: selectedCampaignIds.length })}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>

      <Dialog open={moveMember !== null} onOpenChange={(open) => !open && setMoveMember(null)}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>{t("detail.moveTitle", { name: moveMember?.name })}</DialogTitle>
            <DialogDescription>{t("detail.moveDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 px-6 pb-5">
            {groupsQuery.data
              ?.filter(
                (candidate) =>
                  candidate.id !== group.id &&
                  candidate.lifecycleStatus !== "archived" &&
                  candidate.memberCount < MAX_MANAGED_GROUP_MEMBERS,
              )
              .map((candidate) => (
                <Button
                  key={candidate.id}
                  variant="outline"
                  className="w-full justify-between"
                  disabled={moveMutation.isPending}
                  onClick={() => moveMutation.mutate(candidate.id)}
                >
                  {candidate.name}
                  <span className="text-xs text-muted-foreground">
                    {candidate.memberCount}/{MAX_MANAGED_GROUP_MEMBERS}
                  </span>
                </Button>
              ))}
          </div>
        </DialogPopup>
      </Dialog>

      <Dialog open={removeMember !== null} onOpenChange={(open) => !open && setRemoveMember(null)}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>{t("detail.removeTitle", { name: removeMember?.name })}</DialogTitle>
            <DialogDescription>{t("detail.removeDescription")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveMember(null)}>
              {t("actions.close")}
            </Button>
            <Button
              variant="destructive"
              disabled={!removeMember || removeMutation.isPending}
              onClick={() => removeMember && removeMutation.mutate(removeMember)}
            >
              {t("detail.removeConfirm")}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>

      <EditManagedGroupDialog
        group={{ ...group, members }}
        open={editOpen}
        saving={editMutation.isPending}
        onOpenChange={setEditOpen}
        onSave={(value) => editMutation.mutate(value)}
      />

      <Dialog open={pauseOpen} onOpenChange={setPauseOpen}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>{t("lifecycle.pauseTitle", { name: group.name })}</DialogTitle>
            <DialogDescription>{t("lifecycle.pauseDescription")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPauseOpen(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              disabled={lifecycleMutation.isPending}
              onClick={() => lifecycleMutation.mutate("pause")}
            >
              {t("lifecycle.pauseConfirm")}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>

      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>{t("lifecycle.archiveTitle", { name: group.name })}</DialogTitle>
            <DialogDescription>{t("lifecycle.archiveDescription")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)}>
              {t("actions.close")}
            </Button>
            <Button
              variant="destructive"
              disabled={lifecycleMutation.isPending}
              onClick={() => lifecycleMutation.mutate("archive")}
            >
              {t("lifecycle.archiveConfirm")}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </div>
  );
}

function RecentActivityPanel({
  activity,
  formatDateTime,
  onOpenAll,
}: {
  activity: AiManagedGroupActivity[];
  formatDateTime: (value: string | Date) => string;
  onOpenAll: () => void;
}): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  return (
    <section
      aria-label={t("activity.title")}
      className="shrink-0 overflow-hidden rounded-[14px] border border-line bg-surface"
    >
      <div className="flex min-h-12 items-center justify-between gap-4 px-4 py-2.5">
        <div>
          <h2 className="text-[13px] font-semibold leading-[18px] text-ink">
            {t("activity.recentTitle")}
          </h2>
          <p className="mt-0.5 text-[10px] leading-[15px] text-muted-foreground">
            {t("activity.description")}
          </p>
        </div>
        <Button variant="outline" size="xs" onClick={onOpenAll}>
          {t("activity.openAll")}
        </Button>
      </div>
      {activity.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("activity.campaign")}</TableHead>
              <TableHead>{t("activity.trigger")}</TableHead>
              <TableHead>{t("activity.started")}</TableHead>
              <TableHead>{t("activity.status")}</TableHead>
              <TableHead className="text-right">{t("activity.decisions")}</TableHead>
              <TableHead className="text-right">{t("activity.record")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activity.map((run) => (
              <TableRow key={run.id}>
                <TableCell className="font-medium">{run.campaignName ?? "—"}</TableCell>
                <TableCell>{t(`activity.triggerValues.${run.triggerType}`)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatDateTime(run.startedAt)}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={run.status === "failed" ? "destructive" : "neutral"}
                    size="compact"
                  >
                    {t(`activity.statusValues.${run.status}`)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {run.pendingDecisions}
                </TableCell>
                <TableCell className="text-right">
                  {run.hasDecisionRecord ? (
                    <Button
                      render={<Link to="/ai-operations/$runId" params={{ runId: run.id }} />}
                      variant="ghost"
                      size="xs"
                    >
                      {t("activity.openRecord")}
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="flex min-h-32 flex-col items-center justify-center gap-2 border-t border-line text-muted-foreground">
          <IconActivity className="size-7" />
          <p className="text-xs">{t("activity.empty")}</p>
        </div>
      )}
    </section>
  );
}

function CampaignsPanel({
  shopId,
  lifecycleStatus,
  totalCount,
  filteredCount,
  visibleMembers,
  search,
  aiFilter,
  page,
  pageCount,
  canManage,
  aiChanging,
  currency,
  formatCurrency,
  formatDateTime,
  formatPercent,
  onSearchChange,
  onAiFilterChange,
  onPageChange,
  onAdd,
  onToggleAi,
  onMove,
  onRemove,
}: {
  shopId: string;
  lifecycleStatus: "active" | "paused" | "archived";
  totalCount: number;
  filteredCount: number;
  visibleMembers: AiManagedGroupMemberAnalytics[];
  search: string;
  aiFilter: CampaignAiFilter;
  page: number;
  pageCount: number;
  canManage: boolean;
  aiChanging: boolean;
  currency: string;
  formatCurrency: (value: number, currency: string) => string;
  formatDateTime: (value: string | Date) => string;
  formatPercent: (value: number) => string;
  onSearchChange: (value: string) => void;
  onAiFilterChange: (value: CampaignAiFilter) => void;
  onPageChange: (page: number) => void;
  onAdd: () => void;
  onToggleAi: (member: AiManagedGroupMemberAnalytics) => void;
  onMove: (member: AiManagedGroupMemberAnalytics) => void;
  onRemove: (member: AiManagedGroupMemberAnalytics) => void;
}): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const aiFilterItems = [
    { value: "all", label: t("detail.aiFilter.all") },
    { value: "on", label: t("detail.aiFilter.on") },
    { value: "off", label: t("detail.aiFilter.off") },
  ];
  const from = filteredCount === 0 ? 0 : (page - 1) * MEMBER_PAGE_SIZE + 1;
  const to = Math.min(page * MEMBER_PAGE_SIZE, filteredCount);

  return (
    <section className="flex shrink-0 flex-col overflow-hidden rounded-[14px] border border-line bg-surface">
      <div className="flex min-h-12 flex-wrap items-center justify-between gap-3 px-3.5 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <SearchInput
            showIcon
            className="max-w-[260px]"
            value={search}
            placeholder={t("detail.searchCampaigns")}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          <Select
            items={aiFilterItems}
            value={aiFilter}
            onValueChange={(value) => {
              if (value === "all" || value === "on" || value === "off") {
                onAiFilterChange(value);
              }
            }}
          >
            <SelectTrigger size="sm" className="w-[126px]" aria-label={t("detail.aiFilter.label")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="start" alignItemWithTrigger={false}>
              <SelectGroup>
                {aiFilterItems.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        {canManage ? (
          <Button
            size="sm"
            disabled={lifecycleStatus === "archived" || totalCount >= MAX_MANAGED_GROUP_MEMBERS}
            onClick={onAdd}
          >
            <IconPlus data-icon="inline-start" />
            {t("detail.addCampaigns")}
          </Button>
        ) : null}
      </div>

      {totalCount === 0 ? (
        <EmptyState
          icon={<IconSparkles className="size-12" />}
          title={t("detail.emptyMembers")}
          description={t("detail.emptyMembersDescription")}
        />
      ) : filteredCount === 0 ? (
        <div className="flex min-h-52 items-center justify-center border-t border-line px-6 text-sm text-muted-foreground">
          {t("detail.noCampaignMatches")}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border-t border-line paper-scrollbar">
            <Table className="min-w-[980px]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("detail.campaign")}</TableHead>
                  <TableHead>{t("detail.delivery")}</TableHead>
                  <TableHead>{t("detail.ai")}</TableHead>
                  <TableHead className="text-right">{t("detail.spend")}</TableHead>
                  <TableHead className="text-right">{t("detail.acos")}</TableHead>
                  <TableHead>{t("detail.latestRun")}</TableHead>
                  <TableHead className="text-right">{t("detail.pendingDecisions")}</TableHead>
                  {canManage ? (
                    <TableHead className="text-right">{t("detail.actions")}</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleMembers.map((member) => (
                  <TableRow key={member.assignmentId}>
                    <TableCell>
                      <Link
                        to="/campaigns/$id"
                        params={{ id: member.campaignId }}
                        className="block max-w-[260px] truncate font-medium hover:underline"
                        onClick={() => selectResourceShopForNavigation(shopId)}
                      >
                        {member.name}
                      </Link>
                      {member.targetingType ? (
                        <span className="text-[10px] text-muted-foreground">
                          {t(`detail.targetingValues.${member.targetingType}`)}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          member.status === "enabled"
                            ? "success"
                            : member.status === "paused"
                              ? "warning"
                              : "neutral"
                        }
                        size="compact"
                      >
                        {t(`detail.deliveryValues.${member.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={member.aiManaged}
                          disabled={!canManage || aiChanging}
                          aria-label={t("detail.toggleAi", { name: member.name })}
                          onCheckedChange={() => onToggleAi(member)}
                        />
                        <span className="text-[10px] text-muted-foreground">
                          {member.aiManaged ? t("detail.aiOn") : t("detail.aiOff")}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(member.metrics.spend, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {member.metrics.sales > 0 ? formatPercent(member.metrics.acos) : "—"}
                    </TableCell>
                    <TableCell>
                      {member.latestRun ? (
                        <Link
                          to="/ai-operations/$runId"
                          params={{ runId: member.latestRun.id }}
                          className="text-[10px] text-primary hover:underline"
                        >
                          {formatDateTime(member.latestRun.startedAt)}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {member.pendingDecisions > 0 ? (
                        <Badge variant="warning" size="compact">
                          {member.pendingDecisions}
                        </Badge>
                      ) : (
                        "0"
                      )}
                    </TableCell>
                    {canManage ? (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t("detail.moveCampaign", { name: member.name })}
                            onClick={() => onMove(member)}
                          >
                            <IconArrowsExchange />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t("detail.removeCampaign", { name: member.name })}
                            onClick={() => onRemove(member)}
                          >
                            <IconTrash />
                          </Button>
                        </div>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="border-t border-line px-3.5 py-2.5">
            <DataTablePagination
              density="compact"
              page={page}
              pageCount={pageCount}
              summary={t("detail.pagination", { from, to, total: filteredCount })}
              onPageChange={onPageChange}
            />
          </div>
        </>
      )}
    </section>
  );
}
